import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { Header } from "@/components/Header";
import { PublicFooter } from "@/components/PublicFooter";
import { SEO, SITE_URL } from "@/components/SEO";
import { api } from "@/lib/api";

export default function Journal() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/seo/blog/public").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The Roger Kitchen Journal",
    url: `${SITE_URL}/journal`,
    description: "Expert guides on AI visualization, before-and-after selling, and closing more home improvement jobs.",
  };

  return (
    <div className="min-h-screen" data-testid="journal-page">
      <SEO
        title="The Roger Kitchen Journal — Customize Studio"
        description="Expert guides on AI visualization, before-and-after selling, and closing more home improvement jobs — from Customize Studio."
        canonical={`${SITE_URL}/journal`}
        jsonLd={jsonLd}
      />
      <Header />
      <section className="mx-auto max-w-5xl px-6 py-16 md:px-10 lg:py-24">
        <p className="overline flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> The Roger Kitchen Journal</p>
        <h1 className="mt-3 font-display text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl">
          Sell smarter with AI visualization
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[var(--ink-soft)]">
          Practical guides for contractors and remodelers on before-and-after selling, pricing, and closing more jobs on the first visit.
        </p>

        {posts === null ? (
          <p className="mt-12 text-[var(--ink-soft)]">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-dashed border-[var(--line)] p-10 text-center">
            <p className="font-display text-xl">No articles yet</p>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">Roger is warming up. Check back soon.</p>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {posts.map((p) => (
              <Link key={p.id} to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`}
                className="group rounded-2xl border border-[var(--line)] bg-white p-7 transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--brand)] hover:shadow-sm">
                <div className="flex flex-wrap gap-2">
                  {(p.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="rounded-full bg-[var(--surface-2)] px-2.5 py-0.5 text-[11px] font-medium text-[var(--ink-soft)]">{t}</span>
                  ))}
                </div>
                <p className="mt-3 font-display text-xl leading-snug tracking-tight">{p.title}</p>
                <p className="mt-2 text-sm text-[var(--ink-soft)]">{p.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand)]">
                  Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
